import { motion } from 'framer-motion';
import { FaCode, FaMobile, FaCloud, FaGamepad } from 'react-icons/fa';

const About = () => {
  const highlights = [
    {
      icon: FaMobile,
      title: 'Flutter Development',
      description: '1 year of experience building cross-platform mobile applications with Flutter and Dart.',
    },
    {
      icon: FaCode,
      title: 'Web Technologies',
      description: 'Proficient in HTML, CSS, JavaScript, Node.js, and modern web development frameworks.',
    },
    {
      icon: FaCloud,
      title: 'Cloud & Deployment',
      description: 'Experience with Azure, Firebase, IIS deployment, and cloud-based application hosting.',
    },
    {
      icon: FaGamepad,
      title: 'Game Development',
      description: 'Unity game development experience with C# programming and interactive application design.',
    },
  ];



  return (
    <section id="about" className="py-20 bg-gray-800">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            About <span className="text-gradient">Me</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            I'm a passionate Flutter developer with a strong foundation in mobile app development
            and a diverse skill set spanning web technologies, cloud platforms, and game development.
            I love creating innovative solutions and bringing ideas to life through code.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl font-bold mb-6 text-blue-400">
              My Journey
            </h3>
            <div className="text-gray-300 space-y-4 leading-relaxed">
              <p>
                My development journey began with a fascination for creating digital solutions
                that make a difference. Over the past year, I've specialized in Flutter development,
                building robust cross-platform mobile applications that deliver exceptional user experiences.
              </p>
              <p>
                Beyond mobile development, I've expanded my expertise into web technologies,
                cloud platforms, and even game development with Unity. This diverse background
                allows me to approach problems from multiple angles and create comprehensive solutions.
              </p>
              <p>
                I'm particularly passionate about creating browser extensions that enhance user
                productivity and developing Firebase-integrated applications that scale seamlessly
                in the cloud.
              </p>
            </div>

            <motion.div
              whileHover={{ scale: 1.05 }}
              className="mt-8 inline-block"
            >
              <button
                className="btn-primary"
                onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Let's Connect
              </button>
            </motion.div>
          </motion.div>

          {/* Right Content - Highlights */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="grid gap-6"
          >
            {highlights.map((highlight, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.02, x: 10 }}
                viewport={{ once: true }}
                className="card flex items-start gap-4"
              >
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                    <highlight.icon className="text-xl text-white" />
                  </div>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-white mb-2">
                    {highlight.title}
                  </h4>
                  <p className="text-gray-400 leading-relaxed">
                    {highlight.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
